import { MRT_Row } from "material-react-table";
import type { Job, Deal } from "../../../../../../api/types";
import { isFieldRequired } from "../../../../../../utilities/jobValidation";
import { ColumnContext } from "../ColumnContext";
import { SelectColumnViewModel, SelectChangeOptions, SelectBlurOptions } from "./selectColumn.types";
import { isValidEmptyValue, updateRowCache, computeDealId } from "./selectColumn.helpers";
import { ImportTableColumn } from "../../../../../../utilities/types";

/**
 * Hook that provides the SelectColumn ViewModel
 * @param context - The column context containing state and helpers
 * @param columnId - The column identifier for validation
 * @returns ViewModel with all business logic functions
 */
export function useSelectColumnViewModel<T>(
	context: ColumnContext,
	columnId: ImportTableColumn,
): SelectColumnViewModel<T> {
	/**
	 * Get current value from job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentValue(job: Job, fieldKey: keyof Job, getValue?: (job: Job) => T | null): T | undefined {
		if (!job) return undefined;
		return getValue ? (getValue(job) as T | undefined) : (job[fieldKey] as T | undefined);
	}

	/**
	 * Get display value formatted for Autocomplete
	 */
	function getDisplayValue(currentValue: T | undefined, freeSolo: boolean): string | T {
		return freeSolo ? ((currentValue?.toString() ?? "") as string) : (currentValue as T);
	}

	/**
	 * Compute required state from options/context
	 */
	function getIsRequired(row: MRT_Row<Job>, requiredOption?: boolean | ((allDeals: Deal[]) => boolean)): boolean {
		if (typeof requiredOption === "boolean") {
			return requiredOption;
		} else if (typeof requiredOption === "function") {
			return requiredOption(context.props.allDeals || []);
		} else {
			const rowContext = context.getRowValidationContext(row);
			return isFieldRequired(columnId, rowContext);
		}
	}

	/**
	 * Compute disabled state
	 */
	function getIsDisabled(row: MRT_Row<Job>, isDisabled?: (row: MRT_Row<Job>) => boolean): boolean {
		return isDisabled ? isDisabled(row) : false;
	}

	/**
	 * Handle non-freeSolo onChange for Cell
	 * Desktop passes row for MRT cache updates; mobile omits it
	 */
	function handleSelectChange(
		jobId: string,
		fieldKey: keyof Job,
		newValue: T | null,
		row: MRT_Row<Job> | undefined,
		options: SelectChangeOptions<T>,
	): void {
		const { setValue, updateSubRows } = options;

		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;
		const index = newJobs.indexOf(newJob);

		// Use custom setter if provided, otherwise default behavior
		if (setValue) {
			setValue(newJob, newValue);
		} else {
			(newJob as any)[fieldKey] = newValue;
		}

		// Update specific cache fields if needed (desktop only)
		if (row && fieldKey === "branchIds") {
			row._valuesCache.branchIds = newJob.branchIds;
		}

		newJobs[index] = newJob;
		context.setJobs(newJobs);

		context.validateJobs([newJob], { updateState: true, clearPrevious: false });

		// Call updateSubRows if provided (desktop only, requires row)
		if (row && updateSubRows) {
			updateSubRows(row, fieldKey, newValue, context.props.allDeals || []);
		}
	}

	/**
	 * Handle freeSolo onBlur for Cell
	 * Desktop passes row for MRT cache updates; mobile omits it
	 */
	function handleSelectBlur(
		jobId: string,
		fieldKey: keyof Job,
		newValue: string,
		row: MRT_Row<Job> | undefined,
		options: SelectBlurOptions<T>,
	): void {
		const { isNumber, updateSubRows } = options;

		if (isValidEmptyValue(columnId, context.validationContext, newValue)) return;

		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;
		const index = newJobs.indexOf(newJob);

		// For numeric fields, attempt to convert string to number
		// Store as string if invalid - validateJobs() will handle validation
		if (isNumber) {
			const numValue = Number(newValue);
			(newJob as any)[fieldKey] = isNaN(numValue) ? newValue : numValue;
		} else {
			(newJob as any)[fieldKey] = newValue as unknown as T;
		}

		const finalValue = (newJob as any)[fieldKey];

		// Update cache for specific fields (desktop only)
		if (row) {
			updateRowCache(row, fieldKey, finalValue);
		}

		// Auto-update dealIds using findDeal for all these fields
		newJob.dealIds = [computeDealId(context.findDeal, context.props.allDeals, newJob)];
		if (row) {
			row._valuesCache.dealIds = newJob.dealIds;
		}

		newJobs[index] = newJob;
		context.setJobs(newJobs);
		context.validateJobs([newJob], { updateState: true, clearPrevious: false });

		// Call updateSubRows if provided (desktop only, requires row)
		if (row && updateSubRows) {
			updateSubRows(row, fieldKey, finalValue, context.props.allDeals || []);
		}
	}

	/**
	 * Handle non-freeSolo onChange for AggregatedCell
	 */
	function handleGroupSelectChange(
		row: MRT_Row<Job>,
		fieldKey: keyof Job,
		newValue: T | null,
		options: SelectChangeOptions<T>,
	): void {
		const { setValue } = options;

		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			if (setValue) {
				setValue(job, newValue);
			} else {
				(job as any)[fieldKey] = newValue;
			}
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle freeSolo onBlur for AggregatedCell
	 */
	function handleGroupSelectBlur(row: MRT_Row<Job>, fieldKey: keyof Job, newValue: string, isNumber: boolean): void {
		if (newValue === "") return;

		// For numeric fields, attempt to convert string to number
		// Store as string if invalid - validateJobs() will handle validation
		const numValue = isNumber ? Number(newValue) : NaN;
		const valueToStore = isNumber && isNaN(numValue) ? newValue : isNumber ? numValue : newValue;

		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			(job as any)[fieldKey] = valueToStore as unknown as T;

			// Auto-update dealIds using findDeal
			job.dealIds = [computeDealId(context.findDeal, context.props.allDeals, job)];
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentValue,
		getDisplayValue,
		getIsRequired,
		getIsDisabled,
		handleSelectChange,
		handleSelectBlur,
		handleGroupSelectChange,
		handleGroupSelectBlur,
	};
}
